import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Popover, PopoverContent, PopoverTrigger, Radio, RadioGroup } from '@nextui-org/react';
import { Icon } from '@iconify/react';
import { useExportAiWorkerUsageMutation, useExportConversationsMutation } from '@circuitry-ai/doc-data';
import { CtyButton } from '../button/CtyButton';
import { CtyShowToast, checkType } from '../toast/CtyToast';
import { downloadFileFromUrl } from '../utils/conversationDownloadUtils';

const EXPORT_FILE_TYPES = [
  { value: 'json', label: 'JSON', description: 'Structured data file (.json)' },
  { value: 'csv', label: 'CSV', description: 'Opens in spreadsheets (.csv)' },
  { value: 'xlsx', label: 'Excel', description: 'Excel workbook (.xlsx)' },
  { value: 'pdf', label: 'PDF', description: 'Printable document (.pdf)' },
] as const;

type UsageExportFileType = (typeof EXPORT_FILE_TYPES)[number]['value'];

/** File types each export API supports. */
const API_FILE_TYPES: Record<'usage' | 'conversations', readonly UsageExportFileType[]> = {
  usage: ['json', 'csv', 'xlsx'],
  conversations: ['json', 'csv', 'xlsx', 'pdf'],
};

type UsageQueryExportButtonProps = Readonly<{
  id: string;
  getQueryPayload: () => object;
  fileBaseName: string;
  api?: 'usage' | 'conversations';
  placement?: 'bottom' | 'bottom-start' | 'bottom-end';
}>;

export function UsageQueryExportButton({
  id,
  getQueryPayload,
  fileBaseName,
  api = 'usage',
  placement = 'bottom-start',
}: UsageQueryExportButtonProps) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [fileType, setFileType] = useState<UsageExportFileType>('json');
  const [exportUsage, { isLoading: isUsageExporting }] = useExportAiWorkerUsageMutation();
  const [exportConversations, { isLoading: isConversationExporting }] = useExportConversationsMutation();
  const exportRequest = api === 'conversations' ? exportConversations : exportUsage;
  const isLoading = api === 'conversations' ? isConversationExporting : isUsageExporting;
  const fileTypeOptions = EXPORT_FILE_TYPES.filter((option) => API_FILE_TYPES[api].includes(option.value));
  const selectedFormat = fileTypeOptions.find((option) => option.value === fileType) ?? fileTypeOptions[0];
  const formatLabel = t(selectedFormat.label);

  const handleOpenChange = (open: boolean) => {
    if (!open && isLoading) return;
    setIsOpen(open);
  };

  const handleExport = async () => {
    try {
      const result = await exportRequest({
        file_type: fileType,
        ...(getQueryPayload() as Record<string, unknown>),
      }).unwrap();
      const presignedUrl = result?.presigned_url;
      if (!presignedUrl) {
        throw new Error(t('No download URL returned'));
      }
      try {
        await downloadFileFromUrl(presignedUrl, `${fileBaseName}.${fileType}`);
      } catch {
        return;
      }
      const exported = result?.exported_rows;
      const total = result?.total;
      const message = result?.truncated && exported != null && total != null
        // defaultValue keeps the text interpolated in hosts without an i18next instance (e.g. PAL)
        ? t('Exported {{exported}} of {{total}} rows', { exported, total, defaultValue: `Exported ${exported} of ${total} rows` })
        : t('Export downloaded');
      CtyShowToast(checkType.success, message);
      setIsOpen(false);
    } catch (error) {
      const err = error as { data?: { message?: string }; message?: string };
      CtyShowToast(checkType.error, err?.data?.message || err?.message || t('Failed to export'));
    }
  };

  return (
    <Popover isOpen={isOpen} onOpenChange={handleOpenChange} placement={placement}>
      <PopoverTrigger>
        <Button
          id={id}
          variant="bordered"
          size="sm"
          radius="md"
          className="font-semibold text-sm h-[36px] rounded-md border-1"
          startContent={<Icon icon="mdi:download" className="w-4 h-4" />}
        >
          {t('Export')}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[280px] max-w-[calc(100vw-24px)] items-stretch p-3 bg-content1">
        <div className="flex w-full flex-col gap-3">
          <div>
            <p className="text-sm font-semibold text-foreground">{t('Export results')}</p>
            <p className="mt-1 text-xs text-default-500">
              <span className="sm:hidden">{t('Includes current filters and sort.')}</span>
              <span className="hidden sm:inline">{t('Choose a format. Current filters and sort are included.')}</span>
            </p>
          </div>
          <RadioGroup
            aria-label={t('Export file type')}
            value={fileType}
            onValueChange={(value) => setFileType(value as UsageExportFileType)}
            isDisabled={isLoading}
            // Narrow hosts (PAL widget/extension): formats in one row so the popover fits below the trigger
            classNames={{ wrapper: 'flex-row flex-nowrap justify-between gap-2 sm:flex-col' }}
          >
            {fileTypeOptions.map((option) => (
              <Radio
                key={option.value}
                value={option.value}
                description={t(option.description)}
                classNames={{
                  base: 'm-0 p-0 max-w-full rounded-md sm:w-full sm:border sm:border-divider sm:px-3 sm:py-2 sm:hover:bg-default-100 data-[selected=true]:border-primary',
                  labelWrapper: 'ml-1 sm:ml-2',
                  label: 'text-sm font-medium text-foreground',
                  description: 'hidden sm:block text-xs text-default-500',
                }}
              >
                {t(option.label)}
              </Radio>
            ))}
          </RadioGroup>
          <CtyButton
            id={`${id}-confirm`}
            color="primary"
            size="sm"
            className="w-full"
            isLoading={isLoading}
            onPress={() => {
              void handleExport();
            }}
          >
            {t('Export {{format}}', { format: formatLabel, defaultValue: `Export ${formatLabel}` })}
          </CtyButton>
        </div>
      </PopoverContent>
    </Popover>
  );
}
